import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getProductStats, listProducts, ProductRow, ProductStats } from '../../api/assets';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import Pagination from '../../components/Pagination';
import { toast } from '../../utils/toast';
import { useSyncQueryToUrl } from '../../utils/urlState';
import { BarList, NoticeBar, SegGroup, SortDirToggle, StatCard } from './parts';
import { formatTime } from '../../utils/time';
import { useProductOptions } from './useProductOptions';
import { amazonURL, categoryTail, fmtWeight, MaterialChips, materialList, ProductImg, ProductMainImg, targetText } from './productLabels';
import SearchSelect from '../../components/SearchSelect';
import { sortByDir, SortDir } from '../../utils/sortDesc';

// 数据资产·商品采集中心 (2026-09-08, 需求 wiki 第 3 节):
// 亚马逊商品目录采集 (Keepa), 采集器直写本库五张表, 图片已搬到本平台存储.
// 定位: 忠实沉淀采集字段, **材质**作为耗材识别材料底库来源. 字段稀疏 (采到什么存什么), 列表只展示常见列.
// 统计口径 (需求原文): 采集商品数 / 覆盖品牌 / 覆盖类目; 不统计「材质种类」—— 大量商品无材质字段, 统计会误导.
// 页面: 统计 / 明细 (图墙 | 表格) 双视图, 详情为独立路由页.

const PAGE_SIZE = 24;
const SORT_OPTS = [
    { value: '', label: '按榜单位次' },
    { value: 'sold', label: '按月销量' },
    { value: 'newest', label: '按拉取时间' },
];
const pct = (n: number, d: number) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '—');

function ProductList() {
    const history = useHistory();
    const initQ = new URLSearchParams(useLocation().search);
    const [tab, setTab] = useState(initQ.get('tab') === 'table' ? 'table' : 'stats');
    const [targetDir, setTargetDir] = useState<SortDir>('desc');
    const [view, setView] = useState<'grid' | 'table'>(initQ.get('view') === 'table' ? 'table' : 'grid');
    const [target, setTarget] = useState(initQ.get('target') || '');
    const [categoryPath, setCategoryPath] = useState(initQ.get('categoryPath') || '');
    const [brand, setBrand] = useState(initQ.get('brand') || '');
    const [material, setMaterial] = useState(initQ.get('material') || '');
    const [hasMaterial, setHasMaterial] = useState(initQ.get('hasMaterial') || '');
    const [hasImage, setHasImage] = useState(initQ.get('hasImage') || '');
    const [inScope, setInScope] = useState(initQ.get('inScope') || '');
    const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
    const [keywordDraft, setKeywordDraft] = useState(initQ.get('keyword') || '');
    const [sort, setSort] = useState(initQ.get('sort') || '');
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);

    const [list, setList] = useState<ProductRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState<ProductStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(false);
    // 冷缓存时接口会回 200 + 空数组 + warming, 必须重试 —— 细节见 useProductOptions.
    const options = useProductOptions();
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useSyncQueryToUrl('/assets/products', {
        tab: tab === 'stats' ? '' : tab, view: view === 'grid' ? '' : view,
        target, categoryPath, brand, material, hasMaterial, hasImage, inScope, keyword, sort, page,
    });

    const filterParams = useMemo(() => ({
        target: target || undefined, categoryPath: categoryPath || undefined, brand: brand || undefined, material: material || undefined,
        hasMaterial: hasMaterial || undefined, hasImage: hasImage || undefined, inScope: inScope || undefined, keyword: keyword || undefined,
    }), [target, categoryPath, brand, material, hasMaterial, hasImage, inScope, keyword]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listProducts({ ...filterParams, sort: sort || undefined, page, pageSize: PAGE_SIZE });
            setList(res.list || []);
            setTotal(res.total);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [filterParams, sort, page]);

    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            setStats(await getProductStats(filterParams));
        } catch (e) {
            toast(e instanceof ApiError ? e.message : String(e));
        } finally {
            setStatsLoading(false);
        }
    }, [filterParams]);

    useEffect(() => { if (tab === 'table') load(); }, [load, tab]);
    useEffect(() => { loadStats(); }, [loadStats]);

    const resetFilters = () => {
        setTarget(''); setCategoryPath(''); setBrand(''); setMaterial('');
        setHasMaterial(''); setHasImage(''); setInScope(''); setKeyword(''); setKeywordDraft(''); setSort(''); setPage(1);
    };
    // 下钻: 统计图点一下 → 带条件切到明细.
    const drill = (patch: Partial<{ target: string; categoryPath: string; brand: string; material: string; inScope: string; hasMaterial: string }>) => {
        if (patch.target !== undefined) setTarget(patch.target);
        if (patch.categoryPath !== undefined) setCategoryPath(patch.categoryPath);
        if (patch.brand !== undefined) setBrand(patch.brand);
        if (patch.material !== undefined) setMaterial(patch.material);
        if (patch.inScope !== undefined) setInScope(patch.inScope);
        if (patch.hasMaterial !== undefined) setHasMaterial(patch.hasMaterial);
        setPage(1);
        setTab('table');
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const k = stats?.kpi;
    const filtered = Boolean(target || categoryPath || brand || material || hasMaterial || hasImage || inScope || keyword);
    // 类目路径下拉按路径首级 (亚马逊根类目) 分组, 约 50 条全给.
    const pathGroups = useMemo(() => {
        const g: Record<string, { key: string; count: number }[]> = {};
        (options?.categoryPaths || []).forEach((p) => {
            const root = p.key.split('>')[0].trim();
            (g[root] = g[root] || []).push(p);
        });
        return g;
    }, [options]);
    const brandInList = (options?.brands || []).some((b) => b.key === brand);

    const goDetail = (asin: string) => history.push(`/assets/products/${asin}`);

    return (
        <div className="space-y-5">
            {/* 页头 */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] font-extrabold tracking-[4px] uppercase text-slate-400 mb-1">Amazon · Keepa · Product Catalog</div>
                    <div className="text-xl font-extrabold text-slate-900 tracking-tight">商品采集</div>
                    <div className="text-xs text-slate-500 mt-1.5">
                        亚马逊热销商品目录的采集沉淀 —— 品牌、类目、<b>材质</b>、颜色尺寸重量、商品图。材质字段是材料识别材料底库的来源。
                        <span className="text-slate-400"> 字段稀疏且不统一：采到什么存什么，缺失显示「—」。</span>
                    </div>
                </div>
                <div className="text-right text-[11px] text-slate-400 leading-relaxed shrink-0">
                    <div className="text-slate-500 font-semibold">持续采集 · 采集器直写</div>
                    <div>
                        {stats?.collect ? <>{stats.collect.nodes} 个类目节点 · 最近拉榜 {stats.collect.lastPulled || '—'}</> : '…'}
                    </div>
                </div>
            </div>

            {/* 筛选 (统计与明细共用) */}
            <div className="card px-4 py-3 space-y-2.5">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <SegGroup label="材质" value={hasMaterial} onChange={(v) => { setHasMaterial(v); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'yes', label: '有材质' }, { value: 'no', label: '无材质' }]} />
                    <SegGroup label="商品图" value={hasImage} onChange={(v) => { setHasImage(v); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'yes', label: '有图' }, { value: 'no', label: '无图' }]} />
                    <SegGroup label="类目归属" value={inScope} onChange={(v) => { setInScope(v); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'tree', label: '类目内' }, { value: 'miss', label: '归错类' }]} />
                    <button type="button" className="ml-auto text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded" onClick={resetFilters}>清空</button>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-2.5 border-t border-slate-50">
                    <SearchSelect className="w-40" value={target} onChange={(v) => { setTarget(v); setCategoryPath(''); setPage(1); }}
                        placeholder="全部采集类目" clearable
                        options={(options?.targets || []).map((t) => ({ value: t.key, label: `${targetText(t.key)} · ${t.count.toLocaleString()}` }))} />
                    <SearchSelect className="w-64" value={categoryPath} onChange={(v) => { setCategoryPath(v); setPage(1); }}
                        placeholder="全部亚马逊类目路径" clearable
                        options={Object.entries(pathGroups).flatMap(([root, paths]) => paths.map((p) => {
                            const rest = p.key.split('>').slice(1).join(' › ').trim();
                            return { value: p.key, label: `${rest ? `${root} / ${rest}` : p.key} · ${p.count.toLocaleString()}` };
                        }))} />
                    <SearchSelect className="w-44" value={brand} onChange={(v) => { setBrand(v); setPage(1); }}
                        placeholder="品牌（精确）" clearable creatable
                        options={(options?.brands || []).map((b) => ({ value: b.key, label: `${b.key} · ${b.count.toLocaleString()} 件` }))} />
                    <SearchSelect className="w-44" value={material} onChange={(v) => { setMaterial(v); setPage(1); }}
                        placeholder="材质包含" clearable creatable
                        options={(options?.materials || []).map((m) => ({ value: m.key, label: `${m.key} · ${m.count.toLocaleString()} 件` }))} />
                    <input className="inp" style={{ width: 190 }} placeholder="产品名称关键词（不含 ASIN）…" value={keywordDraft}
                        onChange={(e) => setKeywordDraft(e.target.value)}
                        onBlur={() => { if (keywordDraft.trim() !== keyword) { setKeyword(keywordDraft.trim()); setPage(1); } }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setKeyword(keywordDraft.trim()); setPage(1); } }} />
                    {brand && !brandInList && <span className="text-[11px] text-slate-400">品牌「{brand}」不在 Top 300 下拉里，按精确名匹配</span>}
                </div>
            </div>

            {/* tabs */}
            <div className="flex items-center gap-1">
                {[['stats', '统计'], ['table', '明细']].map(([key, label]) => (
                    <button key={key} type="button"
                        className={`px-[15px] py-1.5 rounded-[9px] text-[13px] font-semibold ${tab === key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        onClick={() => setTab(key)}>
                        {label}
                    </button>
                ))}
                {tab === 'table' && (
                    <div className="ml-auto flex items-center gap-2 text-xs">
                        <SearchSelect className="w-[130px]" value={sort} onChange={(v) => { setSort(v); setPage(1); }}
                            placeholder="按榜单位次" options={SORT_OPTS} />
                        <div className="flex items-center gap-1">
                            {([['grid', '图墙'], ['table', '表格']] as const).map(([key, label]) => (
                                <button key={key} type="button"
                                    className={`px-2.5 py-1 rounded-md ${view === key ? 'bg-slate-200 text-slate-800 font-semibold' : 'text-slate-500 hover:bg-slate-100'}`}
                                    onClick={() => setView(key)}>{label}</button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {tab === 'stats' ? (
                <div className="space-y-4">
                    <NoticeBar>
                        统计口径只有<b>采集商品数 · 覆盖品牌 · 覆盖类目</b>三项是完整可靠的（每条商品都带）。<b>不统计材质种类</b>：大量商品没有材质字段，「材质 Top」只是<b>有材质子集</b>内的分布，分母见卡片。
                        {filtered && <span className="ml-1 text-indigo-500">当前统计已随筛选收敛。</span>}
                        {stats?.generatedAt ? (
                            <span className="ml-1 text-slate-400">数据截至 {formatTime(stats.generatedAt)}（统计每 6 小时后台重算一次，明细列表不受影响，始终是实时的）。</span>
                        ) : null}
                    </NoticeBar>

                    {/* KPI */}
                    <div className="grid grid-cols-5 gap-4">
                        <StatCard label="采集商品数" value={k ? k.total.toLocaleString() : '—'} sub={k ? `${k.targets} 个采集类目 · 归错类 ${k.scopeMiss.toLocaleString()}` : ''} />
                        <StatCard label="覆盖品牌" value={k ? k.brands.toLocaleString() : '—'} sub="按品牌字段去重" accent="text-indigo-600" />
                        <StatCard label="覆盖类目" value={k ? k.categoryPaths.toLocaleString() : '—'} sub="亚马逊类目路径去重" accent="text-indigo-600" />
                        <StatCard label="有材质字段" value={k ? pct(k.withMaterial, k.total) : '—'} sub={k ? `${k.withMaterial.toLocaleString()} 件 · 材料识别可用样本` : ''} accent="text-teal-600" />
                        <StatCard label="有商品图" value={k ? pct(k.withImage, k.total) : '—'}
                            sub={stats ? `${k?.withImage.toLocaleString()} 件 · 唯一图片文件 ${stats.images.files.toLocaleString()}` : ''} accent="text-amber-600" />
                    </div>

                    {/* 按采集类目: 材质覆盖率决定下一批采什么 */}
                    <div className="card p-4">
                        <div className="flex items-baseline gap-2 mb-1">
                            <div className="text-sm font-semibold text-slate-700">按采集类目 × 材质覆盖率</div>
                            <div className="text-[11px] text-slate-400 ml-auto">哪个类目的材质字段最全 · 点击下钻</div>
                            <SortDirToggle value={targetDir} onChange={setTargetDir} />
                        </div>
                        <div className="text-[11px] text-slate-400 mb-3">材质覆盖率高的类目才是材料识别的有效样本源（实测：耳机 80%+，笔电几乎没有，手机的材质多来自混入的手机壳）。</div>
                        {statsLoading && !stats ? <div className="text-xs text-slate-400 py-6 text-center">统计中…</div> : (
                            <table className="w-full text-xs">
                                <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100">
                                    <th className="py-1.5">采集类目</th><th className="py-1.5 text-right">商品数</th><th className="py-1.5 text-right">有材质</th><th className="py-1.5 pl-4" style={{ width: '40%' }}>覆盖率</th>
                                </tr></thead>
                                <tbody>
                                    {sortByDir(stats?.byTarget || [], (t) => t.count, targetDir).map((t) => {
                                        const rate = t.count > 0 ? (t.withMaterial / t.count) * 100 : 0;
                                        return (
                                            <tr key={t.key} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => drill({ target: t.key })}>
                                                <td className="py-2 font-medium text-slate-700">{targetText(t.key)} <span className="text-slate-400 font-mono font-normal">{t.key}</span></td>
                                                <td className="py-2 text-right font-mono text-slate-600">{t.count.toLocaleString()}</td>
                                                <td className="py-2 text-right font-mono text-teal-600">{t.withMaterial.toLocaleString()}</td>
                                                <td className="py-2 pl-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-2.5 bg-slate-100 rounded overflow-hidden"><div className="h-full rounded" style={{ width: `${rate}%`, background: rate >= 60 ? '#14b8a6' : rate >= 20 ? '#f59e0b' : '#cbd5e1' }} /></div>
                                                        <span className={`w-12 text-right font-semibold ${rate >= 60 ? 'text-teal-600' : rate >= 20 ? 'text-amber-600' : 'text-slate-400'}`}>{rate.toFixed(1)}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* 品牌 | 材质 */}
                    <div className="grid grid-cols-2 gap-4">
                        <BarList title="品牌 Top" hint="按商品数 · 点击下钻" labelWidth="w-32" maxRows={15}
                            rows={(stats?.topBrands || []).map((b) => ({ label: b.key, value: b.count, share: pct(b.withMaterial, b.count), onClick: () => drill({ brand: b.key }) }))}
                            footer={<div className="text-[11px] text-slate-400">右侧百分比 = 该品牌商品里有材质字段的比例。</div>} />
                        <BarList title="材质 Top" hint={`仅有材质子集 · 分母 ${k ? k.withMaterial.toLocaleString() : '—'} 件 · 点击下钻`} labelWidth="w-44" maxRows={15}
                            rows={(stats?.topMaterials || []).map((m) => ({ label: m.key, value: m.count, share: pct(m.count, k?.withMaterial || 0), color: 'rgba(20,184,166,.75)', onClick: () => drill({ material: m.key }) }))}
                            footer={<div className="text-[11px] text-slate-400">一件商品多种材质各计一次（materials_json 炸开），是亚马逊卖家填的自由文本，同义写法未归并。</div>} />
                    </div>

                    {/* 类目路径 | 商品类型 */}
                    <div className="grid grid-cols-2 gap-4">
                        <BarList title="亚马逊类目路径 Top" hint="采集来源的类目体系 · 点击下钻" labelWidth="w-52" maxRows={12}
                            rows={(stats?.byCategory || []).map((c) => ({ label: categoryTail(c.key), value: c.count, share: pct(c.withMaterial, c.count), onClick: () => drill({ categoryPath: c.key }) }))}
                            footer={<div className="text-[11px] text-slate-400">只显示路径末级；右侧百分比 = 该类目材质覆盖率。</div>} />
                        <BarList title="商品类型 (binding)" hint="亚马逊 binding 字段 · 100% 覆盖" labelWidth="w-52" maxRows={12}
                            rows={(stats?.byBinding || []).map((b) => ({ label: b.key, value: b.count, share: pct(b.withMaterial, b.count), color: 'rgba(148,163,184,.8)' }))} />
                    </div>

                    {/* 图片库 / 采集进度 / 归属判定 */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="card p-4 text-xs text-slate-600 space-y-1.5">
                            <div className="text-sm font-semibold text-slate-700 mb-1">图片库 <span className="text-[11px] text-slate-400 font-normal">全量口径</span></div>
                            {stats ? (
                                <>
                                    <div className="flex justify-between"><span className="text-slate-400">唯一图片文件</span><b className="font-mono">{stats.images.files.toLocaleString()}</b></div>
                                    <div className="flex justify-between"><span className="text-slate-400">商品 → 图引用</span><b className="font-mono">{stats.images.refs.toLocaleString()}</b></div>
                                    <div className="flex justify-between"><span className="text-slate-400">已传本平台存储</span><b className="font-mono text-teal-600">{stats.images.uploaded.toLocaleString()}</b></div>
                                    <div className="flex justify-between"><span className="text-slate-400">疑似占位图 (small)</span><b className="font-mono text-amber-600">{stats.images.small.toLocaleString()}</b></div>
                                    <div className="flex justify-between"><span className="text-slate-400">下载失败</span><b className="font-mono text-rose-500">{stats.images.failed.toLocaleString()}</b></div>
                                    <div className="text-[11px] text-slate-400 pt-1">同一张图被多个颜色/尺寸变体复用，按文件名去重后省掉约 45% 下载量。</div>
                                </>
                            ) : <div className="text-slate-400">…</div>}
                        </div>
                        <div className="card p-4 text-xs text-slate-600 space-y-1.5">
                            <div className="text-sm font-semibold text-slate-700 mb-1">采集进度</div>
                            {stats ? (
                                <>
                                    <div className="flex justify-between"><span className="text-slate-400">类目节点</span><b className="font-mono">{stats.collect.nodes}</b></div>
                                    <div className="flex justify-between"><span className="text-slate-400">榜单快照行</span><b className="font-mono">{stats.collect.snapshots.toLocaleString()}</b></div>
                                    <div className="flex justify-between"><span className="text-slate-400">最近拉榜</span><b className="font-mono">{stats.collect.lastPulled || '—'}</b></div>
                                    <div className="flex justify-between"><span className="text-slate-400">最近拉详情</span><b className="font-mono">{stats.collect.lastFetched || '—'}</b></div>
                                    <div className="text-[11px] text-slate-400 pt-1">榜单每天变；拉榜后先落占位行，拉到详情才进本页统计。</div>
                                </>
                            ) : <div className="text-slate-400">…</div>}
                        </div>
                        <BarList title="类目归属判定" hint="采集器复筛 Keepa 类目" labelWidth="w-20"
                            rows={(stats?.byScope || []).map((s) => ({
                                label: s.key === 'tree' ? '类目内' : s.key === 'root' ? '仅根类目' : s.key === 'miss' ? '归错类' : '未判定',
                                value: s.count, share: pct(s.count, k?.total || 0),
                                color: s.key === 'tree' ? '#10b981' : s.key === 'miss' ? '#f43f5e' : '#f59e0b',
                                onClick: s.key ? () => drill({ inScope: s.key }) : undefined,
                            }))}
                            footer={<div className="text-[11px] text-slate-400">归错类的应从训练集剔除；但它拦不住卖家故意挂错（手机类目下的手机壳），下游要按标题另行识别。</div>} />
                    </div>
                </div>
            ) : (
                <>
                    {error && <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>}
                    <div className="card">
                        <div className="flex items-center px-4 pt-3 pb-2">
                            <div className="text-sm font-semibold text-slate-700">商品明细</div>
                            <div className="ml-2 text-xs text-slate-400">{filtered ? '已筛选' : '全部商品'} · {sort === 'sold' ? '按月销量' : sort === 'newest' ? '按拉取时间' : '按榜单位次'}</div>
                            <div className="ml-auto text-xs text-slate-400">共 {total.toLocaleString()} 件</div>
                        </div>
                        {loading ? (
                            <div className="text-center text-slate-400 py-12 text-sm">加载中…</div>
                        ) : list.length === 0 ? (
                            <div className="text-center text-slate-400 py-12 text-sm">没有符合条件的商品</div>
                        ) : view === 'grid' ? (
                            <div className="grid gap-3 px-4 pb-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
                                {list.map((p) => {
                                    const mats = materialList(p);
                                    const mainSrcs = p.images.map((im) => im.imageUrl).filter((u): u is string => Boolean(u));
                                    return (
                                        <div key={p.asin} className="group rounded-xl border border-slate-100 overflow-hidden hover:shadow-md hover:border-slate-200 transition cursor-pointer bg-white flex flex-col"
                                            onClick={() => goDetail(p.asin)}>
                                            <div className="relative bg-white border-b border-slate-50" style={{ aspectRatio: '1 / 1' }}>
                                                {mainSrcs.length > 0 ? (
                                                    <ProductMainImg srcs={mainSrcs} className="w-full h-full object-contain p-2"
                                                        onClick={(e, src) => { e.stopPropagation(); setZoomSrc(src); }} />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-[11px] text-slate-400 bg-slate-50">无商品图</div>
                                                )}
                                                {p.bestSellerRank != null && (
                                                    <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-slate-900/80 text-white text-[10px] font-mono">#{p.bestSellerRank}</div>
                                                )}
                                                {(p.imageCount ?? 0) > 1 && (
                                                    <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/50 text-white text-[10px]">{p.imageCount} 图</div>
                                                )}
                                            </div>
                                            <div className="p-2.5 flex-1 flex flex-col">
                                                <div className="text-[12px] text-slate-800 leading-snug" title={p.title || ''}
                                                    style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 34 }}>
                                                    {p.title || <span className="text-slate-400">无标题</span>}
                                                </div>
                                                <div className="mt-1.5 flex items-center gap-1.5 text-[11px]">
                                                    <span className="font-semibold text-slate-700 truncate" title={p.brand || ''}>{p.brand || '—'}</span>
                                                    <span className="ml-auto px-1.5 rounded bg-slate-100 text-slate-500 whitespace-nowrap">{targetText(p.target)}</span>
                                                </div>
                                                <div className="mt-1.5 min-h-[20px]"><MaterialChips items={mats} max={2} /></div>
                                                <div className="mt-auto pt-1.5 text-[10px] text-slate-400 flex gap-2">
                                                    <span className="font-mono">{p.asin}</span>
                                                    {p.color && <span className="truncate" title={p.color}>{p.color}</span>}
                                                    {p.itemWeightG != null && <span className="ml-auto whitespace-nowrap">{fmtWeight(p.itemWeightG)}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-[13px]" style={{ minWidth: 1040 }}>
                                    <thead>
                                        <tr className="text-left text-[10px] font-extrabold uppercase text-slate-400 border-b border-slate-100" style={{ letterSpacing: '1.5px', background: 'rgb(248 250 252 / 60%)' }}>
                                            <th className="px-3 py-2.5 whitespace-nowrap">品牌</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap">类目</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap" style={{ minWidth: 200 }}>产品名称</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap">ASIN</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap">材质</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap">颜色 / 尺寸</th>
                                            <th className="px-3 py-2.5 text-right whitespace-nowrap">重量</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap">产品图片</th>
                                            <th className="px-3 py-2.5 text-right whitespace-nowrap">操作</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.map((p) => {
                                            const mats = materialList(p);
                                            const rest = (p.imageCount ?? 0) - p.images.length;
                                            return (
                                                <tr key={p.asin} className="border-b border-slate-50 cursor-pointer hover:bg-slate-50"
                                                    style={p.inScope === 'miss' ? { boxShadow: 'inset 2px 0 0 #fda4af' } : undefined}
                                                    onClick={() => goDetail(p.asin)}>
                                                    <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap max-w-[110px] truncate" title={p.brand || ''}>{p.brand || <span className="text-slate-300">—</span>}</td>
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        <div className="text-slate-700">{targetText(p.target)}</div>
                                                        <div className="text-[11px] text-slate-400 max-w-[130px] truncate" title={p.categoryPath || ''}>{categoryTail(p.categoryPath)}</div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="text-slate-700 break-words max-w-[230px]" title={p.title || ''} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                            {p.title || <span className="text-slate-400">无标题</span>}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                                                        {p.asin}
                                                        {p.bestSellerRank != null && <div className="text-slate-400">榜 #{p.bestSellerRank}</div>}
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap"><MaterialChips items={mats} max={2} nowrap /></td>
                                                    <td className="px-3 py-2 text-[12px] text-slate-600 max-w-[130px]">
                                                        <div className="truncate" title={p.color || ''}>{p.color || <span className="text-slate-300">—</span>}</div>
                                                        <div className="text-[11px] text-slate-400 truncate" title={p.size || ''}>{p.size || ''}</div>
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-[12px] text-slate-600 whitespace-nowrap">{fmtWeight(p.itemWeightG)}</td>
                                                    <td className="px-3 py-2">
                                                        <div className="flex items-center gap-1">
                                                            {p.images.slice(0, 3).map((im) => im.imageUrl && (
                                                                <ProductImg key={im.filename} src={im.imageUrl} className="rounded-md object-contain bg-white ring-1 ring-slate-100 cursor-zoom-in"
                                                                    style={{ width: 36, height: 36, minWidth: 36 }}
                                                                    onClick={(e) => { e.stopPropagation(); setZoomSrc(im.imageUrl); }} />
                                                            ))}
                                                            {p.images.length > 0 && rest > 0 && <span className="text-[11px] text-slate-400 whitespace-nowrap">+{rest}</span>}
                                                            {p.images.length === 0 && (
                                                                <span className="text-[11px] text-slate-400 whitespace-nowrap" title="采集器已记录图数, 图片文件尚未入库">
                                                                    {(p.imageCount ?? 0) > 0 ? `${p.imageCount} 图待入库` : '—'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 text-right whitespace-nowrap text-xs">
                                                        <button type="button" className="text-blue-600 hover:underline mr-3" onClick={(e) => { e.stopPropagation(); goDetail(p.asin); }}>详情</button>
                                                        <a href={amazonURL(p.asin)} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-slate-800 hover:underline" onClick={(e) => e.stopPropagation()}>原商品 ↗</a>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {totalPages > 1 && (
                            <div className="px-4 py-3 border-t border-slate-100 flex justify-end">
                                <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} />
                            </div>
                        )}
                    </div>
                </>
            )}
            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default ProductList;
